import { Field } from 'ngx-angular-query-builder';
import { FieldMetadataCollectionBuilder, FilterOperators, FieldMetadata, FieldMetadataCollection, FilterFieldProvider, FilterListItem, FilterAvailabilityCallbacks } from '@common/filter/filtering';
import { ArrearsFields } from '../arrears-fields.provider';
import { DashboardIdentifiers } from '@dashboards/dashboard-identifiers';
import { ArrearsModel } from '../models/arrears.model';
import { ArrayToFilterListItemBuilder } from '@common/filter/services/array-to-filter-list-item-builder';

export class ArrearsFilterFieldProvider implements FilterFieldProvider {

  private readonly fieldMetaDataCollection = new FieldMetadataCollection();
  private readonly fieldPredicates: FilterAvailabilityCallbacks;

  constructor(
    private readonly arrears: Array<ArrearsModel>,
    private readonly arrearsFields: ArrearsFields,
    effectiveProduct: string,
    currentDashboard: string,
  ) {
    this.initialiseFields();
    this.fieldPredicates = new FilterAvailabilityCallbacks(currentDashboard, effectiveProduct);

  }

  private initialiseFields() {

    const fieldMetaDataBuilder = new FieldMetadataCollectionBuilder(this.fieldMetaDataCollection);

    const optionBuilder = new ArrayToFilterListItemBuilder(this.arrears);

    optionBuilder.forProperty('ClientParent', 'ClientParentRef', (x: ArrearsModel) => `${x.ClientParentRef} - ${x.ClientParent}`);
    optionBuilder.forProperty('Client', 'ClientRef', (x: ArrearsModel) => `${x.ClientRef} - ${x.Client}`);
    optionBuilder.forProperty('PropertyType', 'PropertyType', (x: ArrearsModel) => `${x.PropertyTypeCode} - ${x.PropertyType}`);
    optionBuilder.forProperty('PropertyGroup', 'PropertyGroup', (x: ArrearsModel) => `${x.PropertyGroupCode} - ${x.PropertyGroup}`);
    optionBuilder.forProperty('PropertyLocation', 'Location', (x: ArrearsModel) => `${x.LocationCode} - ${x.Location}`);
    optionBuilder.forProperty('Property', 'PropertyRef', (x: ArrearsModel) => `${x.PropertyRef} - ${x.Property}`);
    optionBuilder.forProperty('Age', 'Age', (x: ArrearsModel) => `${x.Age}`);
    optionBuilder.forProperty('O/S Amount', 'Total', (x: ArrearsModel) => `${x.Total}`);
    optionBuilder.forProperty('PaymentPlan', 'PaymentPlan', (x: ArrearsModel) => `${x.PaymentPlan}`);
    optionBuilder.forProperty('SubLedger', 'SubLedger', (x: ArrearsModel) => x.SubLedger == 'None' ? x.SubLedger : `${x.SubLedgerCode} -  ${x.SubLedger}`);


    const options = optionBuilder.build();
    const mojoHomeDashboardOnly = () => this.fieldPredicates.includedOnDashboardsForMojoDashboardProduct([DashboardIdentifiers.HomeDashboard]);
    const excludedPropertyDashboard = () => this.fieldPredicates.excludedOnDashboard([DashboardIdentifiers.PropertyDashboard]);

    fieldMetaDataBuilder.add('Client Parent', 'ClientParentRef', 'category', FilterOperators.Scalar, options.get('ClientParent'), mojoHomeDashboardOnly);
    fieldMetaDataBuilder.add('Client', 'ClientRef', 'category', FilterOperators.Scalar, options.get('Client'), mojoHomeDashboardOnly);
    fieldMetaDataBuilder.add('Property Type', 'PropertyType', 'category', FilterOperators.All, options.get('PropertyType'), excludedPropertyDashboard);
    fieldMetaDataBuilder.add('Property Group', 'PropertyGroup', 'category', FilterOperators.All, options.get('PropertyGroup'), excludedPropertyDashboard);
    fieldMetaDataBuilder.add('Location', 'Location', 'category', FilterOperators.All, options.get('PropertyLocation'), excludedPropertyDashboard);
    fieldMetaDataBuilder.add('Property', 'PropertyRef', 'category', FilterOperators.All, options.get('Property'), mojoHomeDashboardOnly);

    const age = {
      name: this.arrearsFields.age.label,
      type: 'category',
      options: [],
      operators: FilterOperators.CategoryDefaults
    };
    const ageOptions = this.arrearsFields.agedDaysFields.map(field => {
      return {
        label: field.label,
        name: field.label,
        value: field.label
      }
    });
    age.options = age.options.concat(ageOptions);
    this.fieldMetaDataCollection.set(age.name, new FieldMetadata(this.arrearsFields.age.fieldName, age));

    fieldMetaDataBuilder.add('O/S Amount', 'Total', 'number', FilterOperators.AdminScalar, options.get('Total'));

    const paymentPlan = {
      name: this.arrearsFields.paymentPlan.label,
      type: 'string',
      operators: FilterOperators.AdminStringDefaults
    };
    this.fieldMetaDataCollection.set(paymentPlan.name, new FieldMetadata(this.arrearsFields.paymentPlan.fieldName, paymentPlan));

    fieldMetaDataBuilder.add('SubLedger', 'SubLedger', 'category', FilterOperators.All, options.get('SubLedger'));
  }


  getFilterFields(): Array<Field> {
    return this.fieldMetaDataCollection.getFilterFields();
  }

  getFilterField(fieldName: string): Field {
    return this.fieldMetaDataCollection.getFilterField(fieldName);
  }

  getFieldNameFromLabel(label: string): string {
    return this.fieldMetaDataCollection.getFieldNameFromLabel(label);
  }

}


